import React, { useState } from 'react';
import { supabase } from '../../../supabaseClient';
import Icon from '../../../components/AppIcon';

const OnboardingModal = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    grade_level: '',
    study_field: '',
    city: '',
    dream_job: ''
  });

  // Listes déroulantes (Adaptées au contexte Maroc)
  const levels = ["Tronc Commun", "1ère Année Bac", "2ème Année Bac", "Post-Bac / Étudiant"];
  // const fields = ["Sciences Maths A", "Sciences Maths B", "Sciences Physiques", "SVT", "Sciences Éco & Gestion", "Lettres & Sciences Humaines", "Technologique (STE/STM)", "Autre"];
  // Liste exhaustive synchronisée
  const fields = [
    "Sciences Mathématiques A",
    "Sciences Mathématiques B",
    "Sciences Physiques",
    "Sciences de la Vie et de la Terre (SVT)",
    "Sciences Agronomiques",
    "Sciences et Technologies Électriques (STE)",
    "Sciences et Technologies Mécaniques (STM)",
    "Sciences Économiques",
    "Sciences de Gestion Comptable",
    "Lettres",
    "Sciences Humaines",
    "Arts Appliqués",
    "Langue Arabe",
    "Sciences de la Chariaâ",
    "Bac Professionnel",
    "Autre"
  ];
  const cities = ["Casablanca", "Rabat", "Marrakech", "Tanger", "Agadir", "Fès", "Meknès", "Oujda", "Autre"];

  const handleUpdate = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          grade_level: formData.grade_level,
          study_field: formData.study_field,
          city: formData.city,
          dream_job: formData.dream_job,
          updated_at: new Date()
        });

      if (error) throw error;
      
      // On prévient le parent que c'est fini
      onComplete(); 
      
    } catch (error) {
      alert("Erreur : " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-card w-full max-w-lg rounded-2xl shadow-interactive border border-border p-8 relative overflow-hidden">
        
        {/* Barre de progression */}
        <div className="absolute top-0 left-0 w-full h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-500" 
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        {/* Titre dynamique */}
        <div className="text-center mb-8 mt-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
            <Icon name={step === 1 ? "User" : step === 2 ? "Book" : "Star"} size={24} />
          </div>
          <h2 className="text-2xl font-heading font-bold">
            {step === 1 && "Faisons connaissance"}
            {step === 2 && "Ton parcours scolaire"}
            {step === 3 && "Tes ambitions"}
          </h2>
          <p className="text-muted-foreground mt-2">
            Ces infos permettront à l'IA de personnaliser tes résultats.
          </p>
        </div>

        {/* ÉTAPE 1 : Ville et Niveau */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium mb-1.5">Dans quelle ville étudies-tu ?</label>
              <select 
                className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                value={formData.city}
                onChange={(e) => handleUpdate('city', e.target.value)}
              >
                <option value="">Sélectionner une ville...</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Ton niveau actuel</label>
              <div className="grid grid-cols-2 gap-3">
                {levels.map(l => (
                  <button
                    key={l}
                    onClick={() => handleUpdate('grade_level', l)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      formData.grade_level === l 
                        ? 'border-primary bg-primary/5 text-primary ring-1 ring-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE 2 : Filière */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in">
            <label className="block text-sm font-medium mb-1.5">Quelle est ta filière ?</label>
            <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
              {fields.map(f => (
                <button
                  key={f}
                  onClick={() => handleUpdate('study_field', f)}
                  className={`p-4 rounded-xl border text-left flex items-center justify-between transition-all ${
                    formData.study_field === f 
                      ? 'border-primary bg-primary/5 text-primary ring-1 ring-primary' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium">{f}</span>
                  {formData.study_field === f && <Icon name="Check" size={16} />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ÉTAPE 3 : Ambitions */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium mb-1.5">Quel est ton métier de rêve ?</label>
              <input 
                type="text" 
                placeholder="Ex: Architecte, Développeur..."
                className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none"
                value={formData.dream_job}
                onChange={(e) => handleUpdate('dream_job', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Si tu ne sais pas encore, tu peux laisser vide ou écrire "Indécis".
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
          {step > 1 ? (
            <button 
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground font-medium px-4 py-2"
            >
              Retour
            </button>
          ) : (
            <div></div> // Spacer vide
          )}

          {step < 3 ? (
            <button 
              onClick={handleNext}
              disabled={
                (step === 1 && (!formData.city || !formData.grade_level)) ||
                (step === 2 && !formData.study_field)
              }
              className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Suivant <Icon name="ArrowRight" size={16} />
            </button>
          ) : (
            <button 
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-smooth flex items-center gap-2 shadow-lg shadow-primary/25"
            >
              {loading ? 'Finalisation...' : 'Terminer mon profil'} <Icon name="Check" size={16} />
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default OnboardingModal;